import React, { useState } from "react";
import { Form, Input, Button, Typography, message, Card, Steps } from "antd";
import { useNavigate } from "react-router-dom";
import { axiosForm } from "../../axiosInstances";
import Logo from "../common/Logo";
import AnimatedBackground from "./AnimatedBackground";

const { Title, Text } = Typography;

const ForgotPasswordForm = () => {
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState(0);
  const [email, setEmail] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const navigate = useNavigate();

  // Step 1: send the reset code
  const onSendCode = async (values) => {
    setLoading(true);
    try {
      await axiosForm.post("/authentication/api/forgot-password/send-code/", {
        email: values.email,
      });
      setEmail(values.email);
      message.success("Un code de vérification a été envoyé à votre adresse email.");
      setCurrent(1);
    } catch (error) {
      message.error(
        error.response?.data?.detail ||
          "Impossible d'envoyer le code. Veuillez réessayer."
      );
    } finally {
      setLoading(false);
    }
  };

  // Step 2: verify the code
  const onVerifyCode = async (values) => {
    setLoading(true);
    try {
      const response = await axiosForm.post("/authentication/api/forgot-password/verify-code/", {
        email,
        code: values.code,
      });
      setVerificationToken(response.data.verification_token);
      message.success("Code vérifié. Choisissez un nouveau mot de passe.");
      setCurrent(2);
    } catch (error) {
      message.error(
        error.response?.data?.detail || "Code invalide ou expiré."
      );
    } finally {
      setLoading(false);
    }
  };

  // Step 3: set the new password
  const onResetPassword = async (values) => {
    setLoading(true);
    try {
      await axiosForm.post("/authentication/api/forgot-password/reset/", {
        email,
        verification_token: verificationToken,
        new_password: values.new_password,
      });
      message.success("Mot de passe réinitialisé avec succès. Connectez-vous.");
      navigate("/login");
    } catch (error) {
      message.error(
        error.response?.data?.detail ||
          "Impossible de réinitialiser le mot de passe."
      );
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    setLoading(true);
    try {
      await axiosForm.post("/authentication/api/forgot-password/send-code/", { email });
      message.success("Un nouveau code a été envoyé.");
    } catch (error) {
      message.error(
        error.response?.data?.detail || "Impossible d'envoyer le code."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100vw",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "transparent",
        position: "relative",
      }}
    >
      <AnimatedBackground />
      <Card
        style={{
          maxWidth: 400,
          width: "100%",
          padding: 24,
          borderRadius: 12,
          boxShadow: "0 2px 16px #0002",
          zIndex: 1,
          background: "#fff",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <Logo size={300} />
        </div>
        <Title level={3} style={{ textAlign: "center" }}>
          Mot de passe oublié
        </Title>
        <Steps
          size="small"
          current={current}
          items={[{ title: "Email" }, { title: "Code" }, { title: "Nouveau" }]}
          style={{ marginBottom: 24 }}
        />

        {current === 0 && (
          <Form layout="vertical" onFinish={onSendCode}>
            <Text type="secondary">
              Saisissez l'adresse email de votre compte. Nous vous enverrons un code de vérification.
            </Text>
            <Form.Item
              label="Adresse email"
              name="email"
              style={{ marginTop: 16 }}
              rules={[
                { required: true, message: "Veuillez saisir votre adresse email" },
                { type: "email", message: "L'email n'est pas valide" },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item>
              <Button block type="primary" htmlType="submit" loading={loading}>
                Envoyer le code
              </Button>
            </Form.Item>
          </Form>
        )}

        {current === 1 && (
          <Form layout="vertical" onFinish={onVerifyCode}>
            <Text type="secondary">
              Saisissez le code à 6 chiffres envoyé à <strong>{email}</strong>.
            </Text>
            <Form.Item
              label="Code de vérification"
              name="code"
              style={{ marginTop: 16 }}
              rules={[
                { required: true, message: "Veuillez saisir le code" },
                { len: 6, message: "Le code contient 6 chiffres" },
              ]}
            >
              <Input maxLength={6} placeholder="______" />
            </Form.Item>
            <Form.Item>
              <Button block type="primary" htmlType="submit" loading={loading}>
                Vérifier le code
              </Button>
            </Form.Item>
            <div style={{ textAlign: "center" }}>
              <Button type="link" onClick={resendCode} disabled={loading}>
                Renvoyer le code
              </Button>
            </div>
          </Form>
        )}

        {current === 2 && (
          <Form layout="vertical" onFinish={onResetPassword}>
            <Form.Item
              label="Nouveau mot de passe"
              name="new_password"
              rules={[
                { required: true, message: "Veuillez saisir un nouveau mot de passe" },
                { min: 6, message: "Au moins 6 caractères" },
              ]}
            >
              <Input.Password />
            </Form.Item>
            <Form.Item
              label="Confirmer le mot de passe"
              name="confirm"
              dependencies={["new_password"]}
              rules={[
                { required: true, message: "Confirmez le mot de passe" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("new_password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Les mots de passe ne correspondent pas"));
                  },
                }),
              ]}
            >
              <Input.Password />
            </Form.Item>
            <Form.Item>
              <Button block type="primary" htmlType="submit" loading={loading}>
                Réinitialiser le mot de passe
              </Button>
            </Form.Item>
          </Form>
        )}

        <div style={{ textAlign: "center", marginTop: 8 }}>
          <a href="/login">Retour à la connexion</a>
        </div>
      </Card>
    </div>
  );
};

export default ForgotPasswordForm;
